import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IEmployee } from 'src/app/employee/employee';
import { environment } from 'src/environments/environment';
import {
  IExpenseDetails,
  ICreateExpenseRequest,
  IUpdateExpenseRequest,
  IRejectExpenseRequest,
  ISearchExpensesRequest,
  IExportExpensesRequest,
  IExpensePagedResults
} from '../models/expense';

@Injectable({
  providedIn: 'root'
})
export class ExpenseService {

  constructor(private http: HttpClient) { }

  searchMyExpenses(request: ISearchExpensesRequest) {
    return this.http.post<IExpensePagedResults>(
      `${environment.baseUrl}/v1/Expenses/SearchMyExpenses`,
      request
    );
  }

  searchExpensesForApproval(request: ISearchExpensesRequest) {
    return this.http.post<IExpensePagedResults>(
      `${environment.baseUrl}/v1/Expenses/SearchExpensesForApproval`,
      request
    );
  }

  searchAccountingExpenses(request: ISearchExpensesRequest) {
    return this.http.post<IExpensePagedResults>(
      `${environment.baseUrl}/v1/Expenses/SearchAccountingExpenses`,
      request
    );
  }

  markExpensePaid(id: string) {
    return this.http.post<string>(
      `${environment.baseUrl}/v1/Expenses/MarkExpensePaid/${id}`,
      {}
    );
  }

  rejectExpenseByAccounting(request: IRejectExpenseRequest) {
    return this.http.post<string>(
      `${environment.baseUrl}/v1/Expenses/RejectExpenseByAccounting/${request.id}`,
      request
    );
  }

  // Server-side Excel export; returns the .xlsx as a blob response (see downloadBlob helper).
  exportExpenses(request: IExportExpensesRequest) {
    return this.http.post<Blob>(
      `${environment.baseUrl}/v1/Expenses/ExportExpenses`,
      request,
      {
        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }

  // True when the current user has a supervisor/HR function (drives review-tab visibility),
  // independent of whether anything is currently pending.
  canReviewExpenses() {
    return this.http.get<boolean>(
      `${environment.baseUrl}/v1/Expenses/CanReviewExpenses`
    );
  }

  // Employees the current user may filter the control list by (subordinates + self, or all for HR).
  getReviewableEmployees() {
    return this.http.get<IEmployee[]>(
      `${environment.baseUrl}/v1/Expenses/GetReviewableEmployees`
    );
  }

  getExpense(id: string) {
    return this.http.get<IExpenseDetails>(
      `${environment.baseUrl}/v1/Expenses/GetExpense/${id}`
    );
  }

  createExpense(request: ICreateExpenseRequest) {
    return this.http.post<string>(
      `${environment.baseUrl}/v1/Expenses/CreateExpense`,
      request
    );
  }

  updateExpense(request: IUpdateExpenseRequest) {
    return this.http.put<string>(
      `${environment.baseUrl}/v1/Expenses/UpdateExpense/${request.id}`,
      request
    );
  }

  submitExpense(id: string) {
    return this.http.post<string>(
      `${environment.baseUrl}/v1/Expenses/SubmitExpense/${id}`,
      {}
    );
  }

  withdrawExpense(id: string) {
    return this.http.post<string>(
      `${environment.baseUrl}/v1/Expenses/WithdrawExpense/${id}`,
      {}
    );
  }

  approveExpense(id: string) {
    return this.http.post<string>(
      `${environment.baseUrl}/v1/Expenses/ApproveExpense/${id}`,
      {}
    );
  }

  rejectExpense(request: IRejectExpenseRequest) {
    return this.http.post<string>(
      `${environment.baseUrl}/v1/Expenses/RejectExpense/${request.id}`,
      request
    );
  }

  deleteExpense(id: string) {
    return this.http.delete<string>(
      `${environment.baseUrl}/v1/Expenses/DeleteExpense/${id}`
    );
  }
}
